import type { AdminEmailDto } from "@shelflist/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CircleCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { Page } from "@/components/page";
import { LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError, api, call } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { adminEmailQuery } from "@/lib/kindle";
import { useMe } from "@/lib/session";

export const Route = createFileRoute("/_app/admin/email")({
  component: AdminEmailPage,
});

const ENV_VARIABLES = [
  "SHELFLIST_SMTP_HOST",
  "SHELFLIST_SMTP_PORT",
  "SHELFLIST_SMTP_USER",
  "SHELFLIST_SMTP_PASSWORD",
  "SHELFLIST_SMTP_FROM",
  "SHELFLIST_SMTP_SECURE",
] as const;

function LinkEmailsStatus({ status }: { status: AdminEmailDto["linkEmails"] | undefined }) {
  const { t } = useTranslation("adminEmail");
  if (!status) return null;
  return (
    <p className="text-sm" data-testid="link-emails-status">
      {status.available ? t("linkEmails.available") : t(`linkEmails.${status.reason}`)}
    </p>
  );
}

export function AdminEmailPage() {
  const { t } = useTranslation("adminEmail");
  const errorMessage = useErrorMessage();
  const { data, error } = useQuery(adminEmailQuery);

  return (
    <Page title={t("title")} description={t("description")} width="narrow">
      {error ? (
        <p role="alert" className="text-destructive">
          {errorMessage(error)}
        </p>
      ) : !data ? (
        <LoadingState />
      ) : data.configured ? (
        <>
          <dl
            className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2"
            aria-label={t("status.title")}
          >
            <dt className="text-muted-foreground">{t("status.title")}</dt>
            <dd className="font-medium">{t("status.configured")}</dd>
            <dt className="text-muted-foreground">{t("status.host")}</dt>
            <dd className="break-all">{data.host}</dd>
            <dt className="text-muted-foreground">{t("status.port")}</dt>
            <dd>{data.port}</dd>
            <dt className="text-muted-foreground">{t("status.security")}</dt>
            <dd>{t(`mode.${data.mode ?? "starttls"}`)}</dd>
            <dt className="text-muted-foreground">{t("status.from")}</dt>
            <dd className="break-all">{data.from}</dd>
            <dt className="text-muted-foreground">{t("status.credentials")}</dt>
            <dd>
              {data.authConfigured ? t("status.credentialsSet") : t("status.credentialsNone")}
            </dd>
          </dl>
          <LinkEmailsStatus status={data.linkEmails} />
          <TestForm />
        </>
      ) : (
        <>
          <p className="font-medium">{t("status.notConfigured")}</p>
          <p className="text-muted-foreground">{t("setup.intro")}</p>
          <ul className="grid gap-1 font-mono text-sm">
            {ENV_VARIABLES.map((name) => (
              <li key={name}>
                <code>{name}</code> <span className="font-sans">{t(`setup.vars.${name}`)}</span>
              </li>
            ))}
          </ul>
          <p className="text-muted-foreground text-sm">{t("setup.testNote")}</p>
          <LinkEmailsStatus status={data.linkEmails} />
        </>
      )}
    </Page>
  );
}

function TestForm() {
  const { t } = useTranslation("adminEmail");
  const me = useMe();
  const errorMessage = useErrorMessage();
  const [to, setTo] = useState("");
  const test = useMutation({
    mutationFn: () =>
      call(api.admin.email.test.$post({ json: to.trim() ? { to: to.trim() } : {} })),
    meta: { handlesErrors: true },
  });
  const failure = test.error instanceof ApiError ? test.error : undefined;
  const fieldError = failure?.status === 400 ? "email_invalid" : undefined;

  return (
    <form
      noValidate
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        test.mutate();
      }}
    >
      <h2 className="text-lg font-semibold">{t("test.title")}</h2>
      <Field
        id="testRecipient"
        label={t("test.recipient")}
        hint={t("test.recipientHint", { email: me.email })}
        error={fieldError}
      >
        <Input
          {...fieldA11y("testRecipient", fieldError)}
          type="email"
          value={to}
          placeholder={me.email}
          onChange={(event) => setTo(event.target.value)}
        />
      </Field>
      <div>
        <Button type="submit" disabled={test.isPending}>
          {t("test.send")}
        </Button>
      </div>
      {test.isSuccess ? (
        <p role="status" className="flex items-start gap-2 text-sm font-medium">
          <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            {t("test.success", { to: test.data.to })}{" "}
            <code className="font-normal break-all">{test.data.response}</code>
          </span>
        </p>
      ) : null}
      {failure && !fieldError ? (
        <>
          <FormError message={errorMessage(failure)} />
          {failure.details.diagnostic ? (
            <p className="text-muted-foreground text-sm break-words">
              {t("test.diagnostic")} <code>{failure.details.diagnostic}</code>
            </p>
          ) : null}
        </>
      ) : null}
    </form>
  );
}
